/**
 * Referral Storage Utilities
 *
 * Provides secure sessionStorage operations for self-referral workflow data.
 * Uses sessionStorage exclusively for patient data privacy and automatic cleanup
 * when browser session ends.
 */

import type {
  StoredReferralData,
  WorkflowState,
  ReferralStorageContext,
  StorageOperationResult,
  StorageCapabilities
} from '@/components/self-referral/types';

// Storage keys
const REFERRAL_DATA_KEY = 'referralData';
const REFERRAL_CONTEXT_KEY = 'referralContext';

/**
 * Detect browser storage capabilities
 */
export const detectStorageCapabilities = (): StorageCapabilities => {
  let hasSessionStorage = false;
  let hasLocalStorage = false;
  let isStorageFunctional = false;

  try {
    // Test sessionStorage availability
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const testKey = '__storage_test__';
      window.sessionStorage.setItem(testKey, 'test');
      window.sessionStorage.removeItem(testKey);
      hasSessionStorage = true;
    }
  } catch {
    hasSessionStorage = false;
  }

  try {
    // Test localStorage availability
    if (typeof window !== 'undefined' && window.localStorage) {
      const testKey = '__storage_test__';
      window.localStorage.setItem(testKey, 'test');
      window.localStorage.removeItem(testKey);
      hasLocalStorage = true;
    }
  } catch {
    hasLocalStorage = false;
  }

  // Storage is functional if at least sessionStorage works
  isStorageFunctional = hasSessionStorage;

  return {
    hasSessionStorage,
    hasLocalStorage,
    isStorageFunctional
  };
};

/**
 * Safely stringify data for storage
 */
const safeStringify = (data: unknown): string | null => {
  try {
    return JSON.stringify(data);
  } catch (error) {
    console.warn('Failed to stringify data for storage:', error);
    return null;
  }
};

/**
 * Safely parse data from storage
 */
const safeParse = <T>(data: string | null): T | null => {
  if (!data) return null;

  try {
    return JSON.parse(data) as T;
  } catch (error) {
    console.warn('Failed to parse data from storage:', error);
    return null;
  }
};

/**
 * Create storage context metadata
 */
const createStorageContext = (
  providerId?: string,
  sessionToken?: string,
  expiresAt?: string
): ReferralStorageContext => ({
  timestamp: new Date().toISOString(),
  providerId,
  sessionToken,
  expiresAt
});

/**
 * Referral storage operations
 */
export const referralStorage = {
  /**
   * Store referral workflow data in sessionStorage
   */
  store: (
    workflowState: WorkflowState,
    providerId?: string,
    sessionToken?: string,
    expiresAt?: string
  ): StorageOperationResult => {
    try {
      const capabilities = detectStorageCapabilities();

      if (!capabilities.isStorageFunctional) {
        return {
          success: false,
          error: 'Storage is not available or functional'
        };
      }

      const context = createStorageContext(providerId, sessionToken, expiresAt);
      const storedData: StoredReferralData = {
        workflow: workflowState,
        context
      };

      const serializedData = safeStringify(storedData);
      const serializedContext = safeStringify(context);

      if (!serializedData || !serializedContext) {
        return {
          success: false,
          error: 'Failed to serialize data for storage'
        };
      }

      sessionStorage.setItem(REFERRAL_DATA_KEY, serializedData);
      sessionStorage.setItem(REFERRAL_CONTEXT_KEY, serializedContext);

      return { success: true };
    } catch (error) {
      console.warn('Failed to store referral data:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown storage error'
      };
    }
  },

  /**
   * Retrieve referral workflow data from sessionStorage
   */
  retrieve: (): StoredReferralData | null => {
    try {
      const capabilities = detectStorageCapabilities();

      if (!capabilities.isStorageFunctional) {
        console.warn('Storage is not available or functional');
        return null;
      }

      const storedData = sessionStorage.getItem(REFERRAL_DATA_KEY);

      if (!storedData) {
        return null;
      }

      const parsedData = safeParse<StoredReferralData>(storedData);

      if (!parsedData) {
        // Clean up corrupted data
        referralStorage.cleanup();
        return null;
      }

      // Validate data structure
      if (!parsedData.workflow || !parsedData.context) {
        console.warn('Invalid referral data structure found');
        referralStorage.cleanup();
        return null;
      }

      return parsedData;
    } catch (error) {
      console.warn('Failed to retrieve referral data:', error);
      // Clean up potentially corrupted data
      referralStorage.cleanup();
      return null;
    }
  },

  /**
   * Retrieve only the workflow state
   */
  retrieveWorkflow: (): WorkflowState | null => {
    const storedData = referralStorage.retrieve();
    return storedData?.workflow || null;
  },

  /**
   * Retrieve only the storage context
   */
  retrieveContext: (): ReferralStorageContext | null => {
    const storedData = referralStorage.retrieve();
    return storedData?.context || null;
  },

  /**
   * Update workflow state while preserving context
   */
  updateWorkflow: (workflowState: WorkflowState): StorageOperationResult => {
    const existingData = referralStorage.retrieve();

    if (!existingData) {
      return {
        success: false,
        error: 'No existing referral data found to update'
      };
    }

    return referralStorage.store(
      workflowState,
      existingData.context.providerId,
      existingData.context.sessionToken,
      existingData.context.expiresAt
    );
  },

  /**
   * Check if stored session has expired
   */
  isSessionExpired: (): boolean => {
    const context = referralStorage.retrieveContext();

    if (!context?.expiresAt) {
      return false; // No expiration set
    }

    try {
      const expirationTime = new Date(context.expiresAt).getTime();
      const currentTime = new Date().getTime();
      return currentTime >= expirationTime;
    } catch (error) {
      console.warn('Failed to parse expiration time:', error);
      return true; // Assume expired if we can't parse
    }
  },

  /**
   * Get time remaining until session expires (in seconds)
   */
  getTimeRemaining: (): number => {
    const context = referralStorage.retrieveContext();

    if (!context?.expiresAt) {
      return 0;
    }

    try {
      const expirationTime = new Date(context.expiresAt).getTime();
      const currentTime = new Date().getTime();

      // Check if the date parsing resulted in NaN
      if (isNaN(expirationTime) || isNaN(currentTime)) {
        console.warn('Failed to calculate time remaining:', new Error('Invalid date'));
        return 0;
      }

      const remainingMs = expirationTime - currentTime;

      return Math.max(0, Math.floor(remainingMs / 1000));
    } catch (error) {
      console.warn('Failed to calculate time remaining:', error);
      return 0;
    }
  },

  /**
   * Check if referral data exists in storage
   */
  hasStoredData: (): boolean => {
    try {
      const capabilities = detectStorageCapabilities();

      if (!capabilities.isStorageFunctional) {
        return false;
      }

      const storedData = sessionStorage.getItem(REFERRAL_DATA_KEY);
      return storedData !== null;
    } catch (error) {
      console.warn('Failed to check for stored data:', error);
      return false;
    }
  },

  /**
   * Get storage size information
   */
  getStorageInfo: (): { size: number; keys: string[] } => {
    try {
      const capabilities = detectStorageCapabilities();

      if (!capabilities.isStorageFunctional) {
        return { size: 0, keys: [] };
      }

      const keys = [REFERRAL_DATA_KEY, REFERRAL_CONTEXT_KEY].filter(key =>
        sessionStorage.getItem(key) !== null
      );

      const size = keys.reduce((total, key) => {
        const item = sessionStorage.getItem(key);
        return total + (item ? item.length : 0);
      }, 0);

      return { size, keys };
    } catch (error) {
      console.warn('Failed to get storage info:', error);
      return { size: 0, keys: [] };
    }
  },

  /**
   * Clean up all referral data from sessionStorage
   */
  cleanup: (): StorageOperationResult => {
    try {
      const capabilities = detectStorageCapabilities();

      if (!capabilities.isStorageFunctional) {
        return {
          success: false,
          error: 'Storage is not available or functional'
        };
      }

      sessionStorage.removeItem(REFERRAL_DATA_KEY);
      sessionStorage.removeItem(REFERRAL_CONTEXT_KEY);

      return { success: true };
    } catch (error) {
      console.warn('Failed to cleanup referral data:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown cleanup error'
      };
    }
  },

  /**
   * Clear expired data automatically
   */
  clearExpiredData: (): StorageOperationResult => {
    if (referralStorage.isSessionExpired()) {
      return referralStorage.cleanup();
    }

    return { success: true };
  }
};

/**
 * Initialize storage utilities and perform cleanup
 * Call this when the application starts
 */
export const initializeReferralStorage = (): void => {
  try {
    // Clear any expired data on initialization
    referralStorage.clearExpiredData();
  } catch (error) {
    console.warn('Failed to initialize referral storage:', error);
  }
};

/**
 * Storage event listener for cross-tab synchronization
 * Note: sessionStorage doesn't trigger storage events across tabs,
 * but this is here for potential future localStorage integration
 */
export const addStorageEventListener = (
  callback: (data: StoredReferralData | null) => void
): (() => void) => {
  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === REFERRAL_DATA_KEY) {
      const data = event.newValue ? safeParse<StoredReferralData>(event.newValue) : null;
      callback(data);
    }
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }

  return () => {}; // No-op for server-side rendering
};

// Export storage keys for testing
export const STORAGE_KEYS = {
  REFERRAL_DATA: REFERRAL_DATA_KEY,
  REFERRAL_CONTEXT: REFERRAL_CONTEXT_KEY
} as const;